/**
 * Telematics Sub-section
 * Advanced fleet visibility analytics with availability scoring
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, RefreshCw, Truck, Activity, Sun, Moon, Clock } from 'lucide-react';
import { OptifleetData, SupatrakData, ApiStatus } from '../../types/fleetDataHub';
import DataTable from '../DataTable';
import { Button } from '../ui/button';
import ControlCentreKPICard from './ControlCentreKPICard';
import KPIDataModal, { ColumnDef } from './KPIDataModal';
import ControlCentreSourceBadge from './ControlCentreSourceBadge';
import { useUseCase } from '../../contexts/UseCaseContext';
import { useDemoMode } from '../../contexts/DemoModeContext';
import { API_ENDPOINTS } from '../../config/api';
import { CHART_PALETTE, STATUS_COLORS } from '../../config/colors';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { redactVehicleId } from '../../utils/demoRedaction';
import {
  formatIngestSourceList,
  telematicsGpsLoadingMessage,
  telematicsRealtimeSubtitle,
} from '../../utils/ingestSourceLabels';
import { countProlongedIdleVehicles, formatProlongedIdleLabel } from '../../utils/countProlongedIdleVehicles';
import {
  summarizeTelematicsAggregates,
  summarizeTelematicsVehicles,
} from '../../utils/telematicsAvailabilityScore';
import {
  formatSpeedMphDetail,
  roundSpeedMph,
  SPEED_MPH_BAND_FAST,
  SPEED_MPH_BAND_MODERATE,
  SPEED_MPH_BAND_SLOW,
} from '../../utils/speedUnits';
import { formatUkAsOf } from '../../lib/calc';
import { Map as MapComponent, MapControls, MapMarker, MarkerContent, MarkerTooltip, MarkerPopup, MapPopup, useMap } from '@/components/ui/map';
import { Badge, type BadgeTone } from '../ui/badge';

// Standardise vehicle reg for display: remove all spaces (e.g. "AB 12 CD" -> "AB12CD")
const standardiseVehicleReg = (s: string | null | undefined): string =>
  (s ?? '').replace(/\s+/g, '').trim() || '-';

/** Title-case known telematics statuses for display; keep raw lowercase in data/logic. */
const formatVehicleStatus = (status: string | null | undefined): string => {
  if (!status) return 'Unknown';
  const normalized = status.trim().toLowerCase();
  const labels: Record<string, string> = {
    moving: 'Moving',
    idle: 'Idle',
    offline: 'Offline',
    stopped: 'Stopped',
    parked: 'Parked',
    online: 'Online',
    active: 'Active',
  };
  if (labels[normalized]) return labels[normalized];
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const DEPOT_MARKER_COLOR = STATUS_COLORS.info;

/** Re-render Last Updated labels every minute between 5-min GPS polls. */
const DATA_AGE_TICK_MS = 60_000;

const isObjectRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null;

/**
 * A telematics vehicle as received from a provider payload: the shared vehicle
 * fields plus the provider-specific extras we read (source, Supatrak LocalTime).
 */
type RawTelematicsVehicle = NonNullable<OptifleetData['vehicles']>[number] & {
  source?: string;
  LocalTime?: string | null;
  localTime?: string | null;
};

const normaliseTelematicsPayload = (input: unknown): (OptifleetData & { vehicles: RawTelematicsVehicle[] }) | null => {
  if (!isObjectRecord(input)) return null;
  const vehicles = Array.isArray((input as { vehicles?: unknown }).vehicles)
    ? ((input as { vehicles?: unknown[] }).vehicles as RawTelematicsVehicle[])
    : [];
  return {
    // `input` is a `Record<string, unknown>` (validated by isObjectRecord above)
    // and may carry through extra OptifleetData fields we don't override here
    // (e.g. driver_count, status_count, export_timestamp). Cast through
    // `unknown` so TypeScript accepts the structural shape without complaining
    // about the unknown → required-number conversion — every field we actually
    // rely on is explicitly coerced below.
    ...(input as unknown as OptifleetData),
    vehicles,
    vehicle_count: Number((input as { vehicle_count?: unknown }).vehicle_count) || vehicles.length,
    moving_vehicles: Number((input as { moving_vehicles?: unknown }).moving_vehicles) || 0,
    idle_vehicles: Number((input as { idle_vehicles?: unknown }).idle_vehicles) || 0,
    stopped_vehicles: Number((input as { stopped_vehicles?: unknown }).stopped_vehicles) || 0,
    offline_vehicles: Number((input as { offline_vehicles?: unknown }).offline_vehicles) || 0,
    position_count: Number((input as { position_count?: unknown }).position_count) || 0,
    alert_count: Number((input as { alert_count?: unknown }).alert_count) || 0,
    avg_speed: Number((input as { avg_speed?: unknown }).avg_speed) || 0,
  };
};

// Format timestamp for Live GPS tooltips/popups (24-hour)
const formatUpdatedTime24h = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
};

// Column definitions for GPS/vehicle KPI detail modal
/** One row of the vehicle-status KPI detail modal. */
interface VehicleStatusRow {
  id: string;
  status: string;
  lastUpdate: string | null;
  speed: number | null;
  source?: string;
  vehicleRegDisplay: string;
  prolonged_idle?: boolean;
  idle_duration_minutes?: number | null;
  idle_duration_truncated?: boolean;
}

const VEHICLE_COLUMNS: ColumnDef<VehicleStatusRow>[] = [
  { key: 'id', header: 'Vehicle Reg', render: (v, row) => <span className="font-mono text-ring">{(row?.vehicleRegDisplay != null ? row.vehicleRegDisplay : standardiseVehicleReg(v as string | null | undefined))}</span> },
  { key: 'status', header: 'Status', render: (v, row) => {
    const s = String(v || '').toLowerCase();
    const prolonged = row?.prolonged_idle === true;
    const label = prolonged
      ? formatProlongedIdleLabel(row?.idle_duration_minutes, row?.idle_duration_truncated === true)
      : formatVehicleStatus(v as string | null | undefined);
    const tone: BadgeTone = s.includes('moving') || s.includes('online') ? 'success'
      : prolonged ? 'warning'
      : s.includes('idle') ? 'warning'
      : s.includes('offline') ? 'destructive'
      : 'neutral';
    return <Badge tone={tone} className="py-1">{label}</Badge>;
  }},
  {
    key: 'speed',
    header: 'Speed',
    align: 'right',
    // API speed is km/h; show mph for UK fleet managers.
    render: (v) => {
      const mph = roundSpeedMph(v != null ? Number(v) : null);
      return mph != null ? `${mph} mph` : '-';
    },
  },
  {
    key: 'lastUpdate',
    header: 'Last Update',
    render: (v) => {
      const formatted = formatUpdatedTime24h(typeof v === 'string' ? v : v != null ? String(v) : null);
      return formatted || '—';
    },
  },
];

export interface TelematicsTotals {
  totalVehicles: number;
  alertCount: number;
  idleVehicles: number;
  /** Currently idle with continuous idle ≥15 min (Samsara lookback). */
  prolongedIdleVehicles: number;
  movingVehicles: number;
  stoppedVehicles: number;
  offlineVehicles: number;
  vehiclesWithGps: number;
  positionCount: number;
  /** Same formula as the Telematics Availability Score KPI. */
  availabilityScore: number;
  /** False when there are no in-use vehicles to score — availabilityScore is a sentinel 0. */
  scorable: boolean;
}

export type TelematicsSource = 'optifleet' | 'supatrak' | 'samsara' | 'both';

interface GPSTrackingProps {
  data: OptifleetData | null;
  supatrakData: SupatrakData | null;
  samsaraData?: OptifleetData | null;
  configuredProviders?: string[];
  apiStatus: ApiStatus | undefined;
  onFetchData: () => void;
  isLoading: boolean;
  /** True during silent 5-minute auto-refresh (does not block the map). */
  isBackgroundRefreshing?: boolean;
  /** ISO timestamp of the last successful live telematics fetch. */
  /** Newest position the live providers returned (server `data_current_as_of`). */
  dataCurrentAsOf?: string | null;
  loadingMessage?: string | null;
  /** When true, data is mock – show Source as "Mock" */
  useMockData?: boolean;
  highlightKPI?: string | null;
  /** Clear parent highlight after alert deep-link is consumed (avoids modal reopen loops) */
  onHighlightConsumed?: () => void;
  /** Report current totals so Fleet Alerts can match the Telematics tab */
  onTelematicsTotals?: (totals: TelematicsTotals) => void;
}


/** How far ahead of the browser clock a position time may be and still read as "Just now". */
const CLOCK_SKEW_TOLERANCE_MS = 2 * 60_000;

export default function GPSTracking({ data, supatrakData, samsaraData = null, configuredProviders = [], onFetchData, isLoading, isBackgroundRefreshing = false, dataCurrentAsOf = null, loadingMessage, useMockData, highlightKPI, onHighlightConsumed, onTelematicsTotals }: GPSTrackingProps) {
  const { useCase } = useUseCase();
  const { isDemoMode } = useDemoMode();
  const telematicsFallbackLoadingMsg = telematicsGpsLoadingMessage(configuredProviders);
  const serviceType = useCase === 'emergency' ? 'emergency_services' : 'logistics';
  const [telematicsSource, setTelematicsSource] = useState<TelematicsSource>('both');
  const [defaultSourceApplied, setDefaultSourceApplied] = useState(false);
  /** Map vin (from optifleet_vehicle_list.csv) to customerVehicleName for Vehicle Reg display (same as Configuration / CarbonReporting) */
  const [vinToCustomerNameMap, setVinToCustomerNameMap] = useState<Map<string, string>>(new Map());
  /** True after the VIN map fetch has completed (success or fail). Used to avoid showing raw 110 then 98 when merging both sources. */
  const [vinMapLoadComplete, setVinMapLoadComplete] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const [selectedDepotIndex, setSelectedDepotIndex] = useState<number | null>(null);
  const [depotData, setDepotData] = useState<Array<{ name: string; coordinates: [number, number]; type: string }>>([]);
  /** Live map basemap — dark by default to match Control Centre chrome. */
  const [mapTheme, setMapTheme] = useState<'light' | 'dark'>('dark');
  /** Bumps every minute so relative Last Updated labels stay current between GPS polls. */
  const [dataAgeTick, setDataAgeTick] = useState(0);

  // KPI detail modal state
  const [kpiModal, setKpiModal] = useState<{ open: boolean; title: string; subtitle?: string; filterFn?: (row: VehicleStatusRow) => boolean }>({ open: false, title: '' });
  const kpiGridRef = useRef<HTMLDivElement>(null);
  const vehicleStatusRef = useRef<HTMLDivElement>(null);

  // Load tenant-scoped depot markers from zeecore
  useEffect(() => {
    const loadDepotData = async () => {
      try {
        const response = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.depots());
        if (response.ok) {
          const data = await response.json();
          setDepotData(data.addresses || []);
          console.log('[GPSTracking] Loaded', data.addresses?.length || 0, 'depots');
        } else {
          console.warn('[GPSTracking] Failed to load depot data:', response.status);
        }
      } catch (error) {
        console.warn('[GPSTracking] Error loading depot data:', error);
      }
    };
    loadDepotData();
  }, []);

  // Tick Last Updated display every minute (GPS still polls every 5 min in FleetDataHub).
  useEffect(() => {
    if (useMockData) return;

    const tick = () => {
      if (document.visibilityState === 'visible') {
        setDataAgeTick((n) => n + 1);
      }
    };

    const intervalId = window.setInterval(tick, DATA_AGE_TICK_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setDataAgeTick((n) => n + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [useMockData]);

  // Load Optifleet vehicle list (vin → customerVehicleName) for Vehicle Reg display, same as Configuration / CarbonReporting
  useEffect(() => {
    setVinMapLoadComplete(false);
    let isCancelled = false;
    authenticatedFetch(API_ENDPOINTS.optifleetVehicleList(serviceType))
      .then((res) => res.json().catch(() => ({})))
      .then((json: { ok?: boolean; rows?: Array<{ vin?: string; customerVehicleName?: string }> }) => {
        if (isCancelled || !json?.ok || !Array.isArray(json.rows)) return;
        const mapping = new Map<string, string>();
        json.rows.forEach((row) => {
          const vin = (row.vin ?? '').trim();
          const customerName = (row.customerVehicleName ?? '').trim();
          const displayName = standardiseVehicleReg(customerName);
          if (vin && displayName && displayName !== '-') {
            const normalizedVin = vin.replace(/\s+/g, '');
            mapping.set(normalizedVin, displayName);
            mapping.set(normalizedVin.toUpperCase(), displayName);
            mapping.set(normalizedVin.toLowerCase(), displayName);
            mapping.set(vin, displayName);
          }
        });
        setVinToCustomerNameMap(mapping);
      })
      .catch(() => { /* optional */ })
      .finally(() => {
        if (!isCancelled) setVinMapLoadComplete(true);
      });
    return () => { isCancelled = true; };
  }, [serviceType]);

  const availableProviders = useMemo(() => {
    if (configuredProviders.length > 0) return configuredProviders;
    const providers: string[] = [];
    if ((data?.vehicles?.length ?? 0) > 0) providers.push('optifleet');
    if ((supatrakData?.vehicles?.length ?? 0) > 0) providers.push('supatrak');
    if ((samsaraData?.vehicles?.length ?? 0) > 0) providers.push('samsara');
    return providers;
  }, [configuredProviders, data, supatrakData, samsaraData]);

  /** Options for Source badge: single label, or dropdown when org has 2+ telematics feeds */
  const telematicsSourceOptions = useMemo(() => {
    const hasOptifleet = availableProviders.includes('optifleet');
    const hasSupatrak = availableProviders.includes('supatrak');
    const hasSamsara = availableProviders.includes('samsara');
    const opts: Array<{ value: string; label: string }> = [];

    // Dual Optifleet + Supatrak orgs (e.g. org123): Supatrak, Optifleet, Supatrak + Optifleet
    if (hasOptifleet && hasSupatrak && !hasSamsara) {
      opts.push(
        { value: 'supatrak', label: formatIngestSourceList(['supatrak']) },
        { value: 'optifleet', label: formatIngestSourceList(['optifleet']) },
        { value: 'both', label: formatIngestSourceList(['supatrak', 'optifleet']) },
      );
      return opts;
    }

    for (const id of availableProviders) {
      opts.push({ value: id, label: formatIngestSourceList([id]) });
    }
    if (hasOptifleet && hasSupatrak) {
      opts.push({ value: 'both', label: formatIngestSourceList(['supatrak', 'optifleet']) });
    }
    return opts;
  }, [availableProviders]);

  useEffect(() => {
    if (defaultSourceApplied || availableProviders.length === 0) return;
    if (availableProviders.length === 1) {
      setTelematicsSource(availableProviders[0] as TelematicsSource);
    } else if (
      availableProviders.includes('optifleet')
      && availableProviders.includes('supatrak')
      && !availableProviders.includes('samsara')
    ) {
      setTelematicsSource('both');
    }
    setDefaultSourceApplied(true);
  }, [availableProviders, defaultSourceApplied]);

  const effectiveTelematicsSource = useMemo((): TelematicsSource => {
    if (
      telematicsSource === 'both'
      && availableProviders.includes('optifleet')
      && availableProviders.includes('supatrak')
      && !availableProviders.includes('samsara')
    ) {
      return 'both';
    }
    if (availableProviders.includes(telematicsSource)) {
      return telematicsSource;
    }
    if (availableProviders.length === 1) {
      return availableProviders[0] as TelematicsSource;
    }
    if (availableProviders.includes('optifleet') && availableProviders.includes('supatrak')) {
      return 'both';
    }
    return (availableProviders[0] as TelematicsSource) ?? telematicsSource;
  }, [telematicsSource, availableProviders]);

  const inferVehicleSource = useCallback((vehicle: { source?: string }): string | undefined => {
    if (vehicle.source) return vehicle.source;
    return effectiveTelematicsSource === 'both' ? undefined : effectiveTelematicsSource;
  }, [effectiveTelematicsSource]);

  // Merge telematics data based on selected source
  const mergedData = useMemo(() => {
    const optifleet = normaliseTelematicsPayload(data);
    const supatrak = normaliseTelematicsPayload(supatrakData);
    const samsara = normaliseTelematicsPayload(samsaraData);
    
    if (effectiveTelematicsSource === 'optifleet') {
      return optifleet;
    } else if (effectiveTelematicsSource === 'supatrak') {
      return supatrak;
    } else if (effectiveTelematicsSource === 'samsara') {
      return samsara;
    } else {
      // Merge both sources
      if (!optifleet && !supatrak) return null;
      if (!optifleet) return supatrak;
      if (!supatrak) return optifleet;
      
      // Deduplicate by a common merge key so we count unique vehicles across sources.
      // Optifleet uses VIN as id; Supatrak uses registration. Use optifleet_vehicle_list (vin→customerVehicleName)
      // to get registration for Optifleet so we can match the same vehicle (e.g. 98 Supatrak + 13 Optifleet with 12 overlap = 99 unique).
      const vehicleMap = new Map<string, RawTelematicsVehicle>();
      const norm = (id: string) => standardiseVehicleReg(id) || (id ?? '').trim() || id;
      const getMergeKey = (v: { id: string }, source: 'optifleet' | 'supatrak'): string => {
        const n = norm(v.id);
        if (source === 'optifleet') {
          const customerName = vinToCustomerNameMap.get(n) ?? vinToCustomerNameMap.get(n.toUpperCase()) ?? vinToCustomerNameMap.get(n.toLowerCase()) ?? vinToCustomerNameMap.get((v.id ?? '').trim());
          return norm(customerName ?? v.id);
        }
        return norm(v.id);
      };

      // Add Optifleet vehicles (merge key = normalized registration when vin→customerVehicleName available)
      optifleet.vehicles?.forEach(v => {
        const key = getMergeKey(v, 'optifleet');
        vehicleMap.set(key, { ...v, source: 'optifleet' });
      });

      // Add Supatrak vehicles (merge key = normalized registration); same key merges to one vehicle
      supatrak.vehicles?.forEach(v => {
        const key = getMergeKey(v, 'supatrak');
        const existing = vehicleMap.get(key);
        if (existing) {
          // Prefer the one with more recent data or better GPS coordinates (use LocalTime for Supatrak when lastUpdate missing)
          const existingTs = existing.lastUpdate ?? (existing as { LocalTime?: string | null }).LocalTime;
          const newTs = v.lastUpdate ?? (v as { LocalTime?: string | null }).LocalTime;
          const existingTime = existingTs ? new Date(existingTs).getTime() : 0;
          const newTime = newTs ? new Date(newTs).getTime() : 0;
          if (newTime > existingTime || (v.latitude && v.longitude && !existing.latitude)) {
            vehicleMap.set(key, { ...v, source: 'supatrak' });
          }
        } else {
          vehicleMap.set(key, { ...v, source: 'supatrak' });
        }
      });

      const mergedVehicles = Array.from(vehicleMap.values());
      return {
        ...optifleet, // Use Optifleet as base
        vehicle_count: mergedVehicles.length,
        vehicles: mergedVehicles,
        moving_vehicles: (optifleet.moving_vehicles || 0) + (supatrak.moving_vehicles || 0),
        idle_vehicles: (optifleet.idle_vehicles || 0) + (supatrak.idle_vehicles || 0),
        stopped_vehicles: (optifleet.stopped_vehicles || 0) + (supatrak.stopped_vehicles || 0),
        offline_vehicles: (optifleet.offline_vehicles || 0) + (supatrak.offline_vehicles || 0),
        position_count: (optifleet.position_count || 0) + (supatrak.position_count || 0),
        alert_count: (optifleet.alert_count || 0) + (supatrak.alert_count || 0),
        avg_speed: optifleet.avg_speed && supatrak.avg_speed 
          ? (optifleet.avg_speed + supatrak.avg_speed) / 2 
          : (optifleet.avg_speed || supatrak.avg_speed || 0),
        api_name: `Merged (${formatIngestSourceList(['optifleet', 'supatrak'])})`
      };
    }
  }, [data, supatrakData, samsaraData, effectiveTelematicsSource, vinToCustomerNameMap]);

  // Extract vehicles with GPS positions for map display - MUST be before early return
  const vehiclesWithGPS = useMemo(() => {
    if (!mergedData?.vehicles) {
      console.log('[GPSTracking] No vehicles array in mergedData');
      return [];
    }
    console.log('[GPSTracking] Total vehicles in mergedData:', mergedData.vehicles.length);
    const getLat = (v: { latitude?: number | null; lat?: number | null }) => v.latitude ?? v.lat;
    const getLng = (v: { longitude?: number | null; lng?: number | null }) => v.longitude ?? v.lng;
    console.log('[GPSTracking] Sample vehicles:', mergedData.vehicles.slice(0, 3).map(v => ({
      id: v.id,
      lat: getLat(v),
      lng: getLng(v),
      status: v.status
    })));

    // First filter for valid GPS (accept latitude/longitude or lat/lng), then deduplicate by vehicle reg
    const filtered = mergedData.vehicles.filter(v => {
      const latitude = getLat(v);
      const longitude = getLng(v);
      const hasValidGPS = latitude != null &&
        longitude != null &&
        !isNaN(latitude) &&
        !isNaN(longitude) &&
        latitude !== 0 &&
        longitude !== 0;
      if (!hasValidGPS) {
        console.log('[GPSTracking] Vehicle filtered out:', v.id, 'lat:', latitude, 'lng:', longitude);
      } else {
        console.log('[GPSTracking] Vehicle with valid GPS:', v.id, 'lat:', latitude, 'lng:', longitude);
      }
      return hasValidGPS;
    });
    
    // Deduplicate by vehicle reg - keep the most recent entry (by lastUpdate)
    const vehicleMap = new Map();
    filtered.forEach(vehicle => {
      const existing = vehicleMap.get(vehicle.id);
      if (!existing ||
          (vehicle.lastUpdate && (!existing.lastUpdate || new Date(vehicle.lastUpdate) > new Date(existing.lastUpdate)))) {
        // Normalize to latitude/longitude for map components
        const latitude = getLat(vehicle);
        const longitude = getLng(vehicle);
        vehicleMap.set(vehicle.id, { ...vehicle, latitude, longitude });
      }
    });

    const uniqueVehicles = Array.from(vehicleMap.values());
    console.log('[GPSTracking] Vehicles with valid GPS (after deduplication):', uniqueVehicles.length, 'from', filtered.length, 'filtered');
    console.log('[GPSTracking] GPS coordinates:', uniqueVehicles.map(v => ({ id: v.id, lat: v.latitude, lng: v.longitude })));
    return uniqueVehicles;
  }, [mergedData?.vehicles]);

  // Calculate map center and bounds from GPS data - MUST be before early return
  // Use vehicle center if available, otherwise fallback to default
  const mapCenter = useMemo(() => {
    if (vehiclesWithGPS.length === 0) {
      // If no vehicles, use default center
      return { lat: 0, lon: 0, zoom: 2 };
    }
    const lats = vehiclesWithGPS.map(v => v.latitude!);
    const lons = vehiclesWithGPS.map(v => v.longitude!);
    const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
    const centerLon = (Math.min(...lons) + Math.max(...lons)) / 2;
    return { lat: centerLat, lon: centerLon, zoom: 12 };
  }, [vehiclesWithGPS]);

  // Component to handle map interactions (zoom to vehicle, auto-zoom to all vehicles and depots)
  const MapController = ({ vehicles, selectedVehicleId, depots, selectedDepotIndex }: { vehicles: typeof vehiclesWithGPS; selectedVehicleId: string | null; depots: typeof depotData; selectedDepotIndex: number | null }) => {
    const { map, isLoaded } = useMap();

    // Handle zooming to selected vehicle
    useEffect(() => {
      if (!isLoaded || !map || !selectedVehicleId) return;

      const vehicle = vehicles.find(v => v.id === selectedVehicleId);
      if (vehicle && vehicle.latitude != null && vehicle.longitude != null) {
        map.flyTo({
          center: [vehicle.longitude, vehicle.latitude],
          zoom: 15,
          duration: 1000,
        });
      }
    }, [isLoaded, map, selectedVehicleId, vehicles]);

    // Handle zooming to selected depot
    useEffect(() => {
      if (!isLoaded || !map || selectedDepotIndex === null) return;

      const depot = depots[selectedDepotIndex];
      if (depot) {
        const [lat, lon] = depot.coordinates;
        map.flyTo({
          center: [lon, lat],
          zoom: 15,
          duration: 1000,
        });
      }
    }, [isLoaded, map, selectedDepotIndex, depots]);

    // Auto-zoom: fit all GPS points (vehicles) in view when first loading
    useEffect(() => {
      if (!isLoaded || !map || selectedVehicleId || selectedDepotIndex !== null) return;

      // Fit all vehicles in view
      const allPoints: Array<{ lat: number; lon: number }> = [];
      
      // Add vehicle positions
      vehicles.forEach(v => {
        if (v.latitude != null && v.longitude != null) {
          allPoints.push({ lat: v.latitude, lon: v.longitude });
        }
      });

      depots.forEach(depot => {
        const [lat, lon] = depot.coordinates;
        allPoints.push({ lat, lon });
      });

      if (allPoints.length === 0) return;

      const lats = allPoints.map(p => p.lat);
      const lons = allPoints.map(p => p.lon);

      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLon = Math.min(...lons);
      const maxLon = Math.max(...lons);

      // Add padding around the bounds
      const latPadding = (maxLat - minLat) * 0.1 || 0.01;
      const lonPadding = (maxLon - minLon) * 0.1 || 0.01;

      const bounds = [
        [minLon - lonPadding, minLat - latPadding], // Southwest
        [maxLon + lonPadding, maxLat + latPadding], // Northeast
      ] as [[number, number], [number, number]];

      // Fit map to bounds with padding to show all GPS points
      map.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        duration: 500,
      });
    }, [isLoaded, map, vehicles, depots, selectedVehicleId, selectedDepotIndex]);

    return null;
  };

  // Handle vehicle row click
  const handleVehicleRowClick = useCallback((vehicleId: string) => {
    // Check if vehicle has valid GPS data
    const vehicle = mergedData?.vehicles?.find(v => v.id === vehicleId);
    if (vehicle && vehicle.latitude != null && vehicle.longitude != null && 
        !isNaN(vehicle.latitude) && !isNaN(vehicle.longitude) &&
        vehicle.latitude !== 0 && vehicle.longitude !== 0) {
      setSelectedVehicleId(vehicleId);
    } else {
      console.log('[GPSTracking] Vehicle', vehicleId, 'does not have valid GPS data');
    }
  }, [mergedData?.vehicles]);

  const useFilteredCounts = Array.isArray(mergedData?.vehicles);
  // Total Vehicles: full deduplicated list (stale units still "in fleet").
  const totalVehicles = (mergedData?.vehicles?.length ?? mergedData?.vehicle_count) ?? 0;
  // When source is "both", wait for VIN map load so merge/dedupe is final; avoid flashing 110 then 98
  const totalsStable = effectiveTelematicsSource !== 'both' || vinMapLoadComplete;
  const alertCount = mergedData?.alert_count || 0;
  const positionCount = mergedData?.position_count || 0;

  const availabilitySummary = useMemo(() => {
    if (useFilteredCounts) {
      return summarizeTelematicsVehicles(mergedData?.vehicles ?? [], {
        alertCount,
        positionCount,
      });
    }
    return summarizeTelematicsAggregates({
      totalVehicles,
      movingVehicles: mergedData?.moving_vehicles || 0,
      idleVehicles: mergedData?.idle_vehicles || 0,
      stoppedVehicles: mergedData?.stopped_vehicles || 0,
      offlineVehicles: mergedData?.offline_vehicles || 0,
      alertCount,
      positionCount,
    });
  }, [
    useFilteredCounts,
    mergedData?.vehicles,
    mergedData?.moving_vehicles,
    mergedData?.idle_vehicles,
    mergedData?.stopped_vehicles,
    mergedData?.offline_vehicles,
    totalVehicles,
    alertCount,
    positionCount,
  ]);

  const movingVehicles = availabilitySummary.movingVehicles;
  const idleVehicles = availabilitySummary.idleVehicles;
  const prolongedIdleVehicles = countProlongedIdleVehicles(mergedData);
  const stoppedVehicles = availabilitySummary.stoppedVehicles;
  const offlineVehicles = availabilitySummary.offlineVehicles;
  const activeVehicles = movingVehicles + idleVehicles;
  const availabilityScore = availabilitySummary.availabilityScore;

  // Report totals to parent so Control Centre uses the same Availability Score.
  useEffect(() => {
    if (!totalsStable) return;
    onTelematicsTotals?.({
      totalVehicles,
      alertCount,
      idleVehicles,
      prolongedIdleVehicles,
      movingVehicles,
      stoppedVehicles,
      offlineVehicles,
      vehiclesWithGps: availabilitySummary.vehiclesWithGps,
      positionCount,
      availabilityScore,
      scorable: availabilitySummary.scorable,
    });
  }, [
    onTelematicsTotals,
    totalsStable,
    totalVehicles,
    alertCount,
    idleVehicles,
    prolongedIdleVehicles,
    movingVehicles,
    stoppedVehicles,
    offlineVehicles,
    availabilitySummary.vehiclesWithGps,
    availabilitySummary.scorable,
    positionCount,
    availabilityScore,
  ]);

  // Deep-link from Control Centre Fleet Alerts / System Health.
  // Wait for stable totals so prolonged-idle filters are not empty on first paint.
  useEffect(() => {
    if (!highlightKPI) return;
    if (highlightKPI === 'prolonged-idle' && !totalsStable) return;
    const timer = setTimeout(() => {
      if (highlightKPI === 'vehicle-status') {
        vehicleStatusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        onHighlightConsumed?.();
        return;
      }
      kpiGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (highlightKPI === 'prolonged-idle') {
        setKpiModal({
          open: true,
          title: 'Idling ≥15 min',
          subtitle: `${prolongedIdleVehicles} vehicle${prolongedIdleVehicles === 1 ? '' : 's'} still idle for 15+ minutes`,
          filterFn: (v) => v.prolonged_idle === true,
        });
      } else if (highlightKPI === 'active-vehicles') {
        setKpiModal({
          open: true,
          title: 'Active Vehicles',
          subtitle: `${activeVehicles} of ${totalVehicles} vehicles active`,
          filterFn: (v) => /moving|idle|online|active/i.test(String(v.status || '')),
        });
      } else if (highlightKPI === 'alert-status') {
        // Legacy deep-link — Alert Status KPI was replaced by prolonged idle.
        onHighlightConsumed?.();
        return;
      } else {
        setKpiModal({
          open: true,
          title: 'All Vehicles',
          subtitle: `${totalVehicles} vehicles tracked in fleet`,
        });
      }
      onHighlightConsumed?.();
    }, 400);
    return () => clearTimeout(timer);
  }, [highlightKPI, totalsStable, totalVehicles, alertCount, activeVehicles, prolongedIdleVehicles, onHighlightConsumed]);

  // Resolve Vehicle Reg display: Optifleet uses customerVehicleName from optifleet_vehicle_list.csv (vin column → customerVehicleName)
  // MUST be before early return to satisfy Rules of Hooks
  const getVehicleRegDisplay = useCallback((vehicleId: string, source: string | undefined): string => {
    if (source !== 'optifleet') return standardiseVehicleReg(vehicleId);
    const n = (vehicleId ?? '').trim().replace(/\s+/g, '');
    return vinToCustomerNameMap.get(n) ?? vinToCustomerNameMap.get(n.toUpperCase()) ?? vinToCustomerNameMap.get(n.toLowerCase()) ?? vinToCustomerNameMap.get((vehicleId ?? '').trim()) ?? standardiseVehicleReg(vehicleId);
  }, [vinToCustomerNameMap]);

  // If no data, show fetch prompt - MUST be after all hooks
  if (!mergedData) {
    return (
      <div className="text-center py-12">
        <MapPin className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Fleet Visibility Intelligence</h3>
        <p className="text-muted-foreground mb-6">Advanced GPS tracking analytics, fleet availability scoring, and telematics insights</p>
        {isLoading ? (
          <div className="flex flex-col items-center gap-3">
            <Button
              variant="default"
              size="md"
              className="px-6 shadow-lg bg-ring/50"
              disabled
            >
              <RefreshCw className="w-5 h-5 animate-spin" />
              Loading...
            </Button>
            <p className="text-sm text-ring animate-pulse mt-2 max-w-md">
              {loadingMessage || telematicsFallbackLoadingMsg}
            </p>
          </div>
        ) : (
          <Button
            variant="default"
            size="md"
            className="px-6"
            onClick={onFetchData}
          >
            <RefreshCw className="w-5 h-5" />
            Load GPS Data
          </Button>
        )}
      </div>
    );
  }

  // Calculate derived metrics
  const activeRate = totalVehicles > 0 ? (activeVehicles / totalVehicles) * 100 : 0;

  // Generate vehicle status data for table
  const getStatusChip = (
    status: string,
    opts?: {
      prolongedIdle?: boolean;
      idleDurationMinutes?: number | null;
      idleDurationTruncated?: boolean;
    },
  ) => {
    const statusLower = status.toLowerCase();
    // Tone defaults to neutral; only real status branches override it.
    // Explicit field, not string-sniffed from the tone, so a future status
    // branch can't silently fall back to the wrong colour.
    let tone: BadgeTone = 'neutral';
    let description = '';
    let label = formatVehicleStatus(status);

    switch (statusLower) {
      case 'moving':
        tone = 'success';
        description = 'Vehicle is currently in motion';
        break;
      case 'idle':
        if (opts?.prolongedIdle) {
          const mins = opts.idleDurationMinutes;
          const truncated = opts.idleDurationTruncated === true;
          label = formatProlongedIdleLabel(mins, truncated);
          tone = 'warning';
          description =
            mins != null && Number.isFinite(mins)
              ? (truncated
                ? `Still idle for at least ${Math.floor(mins)} minutes (lookback limited; engine on / not moving)`
                : `Still idle for about ${Math.floor(mins)} minutes (engine on / not moving)`)
              : 'Still idle for 15 minutes or more (engine on / not moving)';
        } else {
          tone = 'warning';
          description = 'Online with speed at or below about 1 mph (e.g. traffic, loading, waiting)';
        }
        break;
      case 'stopped':
      case 'parked':
        tone = 'neutral';
        description = 'Engine off, vehicle is parked';
        break;
      case 'offline':
        tone = 'destructive';
        description = 'No GPS signal or connection lost';
        break;
      default:
        tone = 'neutral';
        description = 'Unknown status';
    }

    return <StatusChipWithTooltip status={label} tone={tone} description={description} />;
  };

  // Component for status chip with tooltip
  const StatusChipWithTooltip = ({ status, tone, description }: { status: string; tone: BadgeTone; description: string }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
    const chipRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
      const handleMouseEnter = () => {
        if (chipRef.current) {
          const rect = chipRef.current.getBoundingClientRect();
          setTooltipPosition({
            x: rect.left + rect.width / 2,
            y: rect.top - 8
          });
          setShowTooltip(true);
        }
      };

      const handleMouseLeave = () => {
        setShowTooltip(false);
        setTooltipPosition(null);
      };

      const chipElement = chipRef.current;
      if (chipElement) {
        chipElement.addEventListener('mouseenter', handleMouseEnter);
        chipElement.addEventListener('mouseleave', handleMouseLeave);
      }

      return () => {
        if (chipElement) {
          chipElement.removeEventListener('mouseenter', handleMouseEnter);
          chipElement.removeEventListener('mouseleave', handleMouseLeave);
        }
      };
    }, []);

    return (
      <>
        <Badge
          ref={chipRef}
          tone={tone}
          className="px-3 py-1 cursor-help"
        >
          {status}
        </Badge>
        {showTooltip && tooltipPosition && createPortal(
          <div
            className="fixed px-3 py-2 bg-popover text-white text-xs font-medium rounded-lg shadow-xl pointer-events-none z-float border border-border whitespace-nowrap"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              transform: 'translate(-50%, -100%)'
            }}
          >
            {description}
          </div>,
          document.body
        )}
      </>
    );
  };

  // Helper function to calculate and format last updated
  const getDataAge = (lastUpdate: string | null | undefined): { display: string; sortKey: number; color: string } => {
    if (!lastUpdate) {
      return { display: 'No data', sortKey: Infinity, color: 'text-muted-foreground' };
    }
    
    try {
      const updateTime = new Date(lastUpdate).getTime();
      const now = Date.now();
      const ageMs = now - updateTime;
      
      // A browser clock a little behind the provider's: the ping is current.
      // Further ahead is a time we cannot place, so it is not shown as fresh.
      if (ageMs < -CLOCK_SKEW_TOLERANCE_MS) {
        return { display: 'Time ahead of clock', sortKey: Infinity, color: 'text-muted-foreground' };
      }
      if (ageMs < 0) {
        return { display: 'Just now', sortKey: 0, color: 'text-success' };
      }
      
      const ageSeconds = Math.floor(ageMs / 1000);
      const ageMinutes = Math.floor(ageSeconds / 60);
      const ageHours = Math.floor(ageMinutes / 60);
      const ageDays = Math.floor(ageHours / 24);
      
      let display: string;
      let color: string;
      
      if (ageMinutes < 1) {
        display = 'Just now';
        color = 'text-success';
      } else if (ageMinutes < 60) {
        display = `${ageMinutes} min${ageMinutes > 1 ? 's' : ''} ago`;
        color = ageMinutes < 15 ? 'text-success' : ageMinutes < 60 ? 'text-warning' : 'text-warning';
      } else if (ageHours < 24) {
        display = `${ageHours} hour${ageHours > 1 ? 's' : ''} ago`;
        color = ageHours < 2 ? 'text-warning' : 'text-warning';
      } else if (ageDays < 7) {
        display = `${ageDays} day${ageDays > 1 ? 's' : ''} ago`;
        color = 'text-destructive';
      } else {
        display = `${ageDays} days ago`;
        color = 'text-destructive';
      }
      
      return { display, sortKey: ageMs, color };
    } catch {
      return { display: 'Invalid date', sortKey: Infinity, color: 'text-muted-foreground' };
    }
  };

  // Format API speed (km/h) as mph with colour bands (~20 / 60 / 100 km/h).
  const formatSpeed = (speedKph: number | null | undefined): { display: JSX.Element; sortKey: number } => {
    const speedMph = roundSpeedMph(speedKph);
    if (speedMph == null) {
      return {
        display: <span className="text-muted-foreground">—</span>,
        sortKey: -1,
      };
    }

    let color: string;
    if (speedMph === 0) {
      color = 'text-muted-foreground';
    } else if (speedMph <= SPEED_MPH_BAND_SLOW) {
      color = 'text-ring';
    } else if (speedMph <= SPEED_MPH_BAND_MODERATE) {
      color = 'text-success';
    } else if (speedMph <= SPEED_MPH_BAND_FAST) {
      color = 'text-warning';
    } else {
      color = 'text-warning';
    }

    return {
      display: <span className={color}>{speedMph} mph</span>,
      sortKey: speedMph,
    };
  };

  // Get vehicle list from mergedData, or generate fallback if not available
  const vehicleStatusList: Array<{
    id: string;
    status: string;
    lastUpdate: string | null;
    speed: number | null;
    source?: string;
    vehicleRegDisplay: string;
    prolonged_idle?: boolean;
    idle_duration_minutes?: number | null;
    idle_duration_truncated?: boolean;
  }> = (() => {
    // Debug: Log what we're receiving
    console.log('[GPSTracking] Full mergedData object:', mergedData);
    console.log('[GPSTracking] mergedData.vehicles:', mergedData?.vehicles);
    console.log('[GPSTracking] mergedData.vehicles type:', typeof mergedData?.vehicles);
    console.log('[GPSTracking] mergedData.vehicles is array?', Array.isArray(mergedData?.vehicles));
    console.log('[GPSTracking] mergedData.vehicles length:', Array.isArray(mergedData?.vehicles) ? mergedData.vehicles.length : 'not an array');
    console.log('[GPSTracking] Full mergedData object keys:', mergedData ? Object.keys(mergedData) : 'no data');
    console.log('[GPSTracking] Has vehicles key?', mergedData ? 'vehicles' in mergedData : 'no data');
    
    const hasVehiclesArray = Array.isArray(mergedData?.vehicles);
    
    // Check if vehicles array exists and has data
    if (mergedData && hasVehiclesArray && mergedData.vehicles.length > 0) {
      console.log('[GPSTracking] Using real vehicle data, count:', mergedData.vehicles.length);
      // Use real vehicle data from API - include lastUpdate and speed
      // Deduplicate by vehicle reg to ensure each vehicle appears only once
      const vehicleMap = new Map();
      mergedData.vehicles.forEach(vehicle => {
        const vehicleId = isDemoMode ? redactVehicleId(vehicle.id, isDemoMode) : vehicle.id;
        // Use lastUpdate or Supatrak's LocalTime (e.g. "2026-02-06T15:54:03") for Last Updated; check multiple keys for API/casing variance
        const raw = vehicle as {
          lastUpdate?: string | null;
          LocalTime?: string | null;
          localTime?: string | null;
          prolonged_idle?: boolean;
          idle_duration_minutes?: number | null;
          idle_duration_truncated?: boolean;
        };
        const timestampForAge = raw.lastUpdate ?? raw.LocalTime ?? raw.localTime ?? null;
        // Keep the most recent entry if duplicate (prefer one with newer timestamp)
        const existing = vehicleMap.get(vehicleId);
        if (!existing || 
            (timestampForAge && (!existing.lastUpdate || new Date(timestampForAge) > new Date(existing.lastUpdate)))) {
          // Single-source mode (Optifleet Only / Supatrak Only) does not set vehicle.source; infer from telematicsSource so Vehicle Reg uses customerVehicleName for Optifleet
          const source = inferVehicleSource(vehicle as { source?: string });
          vehicleMap.set(vehicleId, {
            id: vehicleId,
            status: formatVehicleStatus(vehicle.status),
            lastUpdate: timestampForAge,
            speed: vehicle.speed != null ? vehicle.speed : null,
            source,
            prolonged_idle: raw.prolonged_idle === true,
            idle_duration_minutes: raw.idle_duration_minutes ?? null,
            idle_duration_truncated: raw.idle_duration_truncated === true,
          });
        }
      });
      
      const realVehicles = Array.from(vehicleMap.values());
      console.log('[GPSTracking] Mapped vehicles (after deduplication):', realVehicles.length, 'from', mergedData.vehicles.length, 'total');
      return realVehicles.map((v) => ({ ...v, vehicleRegDisplay: getVehicleRegDisplay(v.id, v.source) }));
    } else if (mergedData && hasVehiclesArray && mergedData.vehicles.length === 0) {
      // Vehicles array exists but is empty - this means backend found no vehicle data
      console.warn('[GPSTracking] Vehicles array exists but is empty. Backend found no vehicle data from positions or vehicle list.');
      return [];
    } else {
      // Vehicles array doesn't exist or is not an array - use fallback
      console.warn('[GPSTracking] No vehicles array in mergedData or it is not an array. Using fallback based on counts. mergedData.vehicles:', mergedData?.vehicles);
      // Fallback: generate vehicles based on counts if real data not available
      const vehicles: Array<{
        id: string;
        status: string;
        lastUpdate: null;
        speed: null;
        vehicleRegDisplay: string;
        prolonged_idle?: boolean;
      }> = [];
      let vehicleIndex = 1;

      // Add moving vehicles
      for (let i = 0; i < movingVehicles; i++) {
        const vehicleId = `VEH-${String(vehicleIndex).padStart(3, '0')}`;
        const id = isDemoMode ? redactVehicleId(vehicleId, isDemoMode) : vehicleId;
        vehicles.push({
          id,
          status: 'Moving',
          lastUpdate: null,
          speed: null,
          vehicleRegDisplay: standardiseVehicleReg(id),
        });
        vehicleIndex++;
      }

      // Add idle vehicles
      for (let i = 0; i < idleVehicles; i++) {
        const vehicleId = `VEH-${String(vehicleIndex).padStart(3, '0')}`;
        const id = isDemoMode ? redactVehicleId(vehicleId, isDemoMode) : vehicleId;
        vehicles.push({
          id,
          status: 'Idle',
          lastUpdate: null,
          speed: null,
          vehicleRegDisplay: standardiseVehicleReg(id),
          prolonged_idle: i < prolongedIdleVehicles,
        });
        vehicleIndex++;
      }

      // Add stopped vehicles
      for (let i = 0; i < stoppedVehicles; i++) {
        const vehicleId = `VEH-${String(vehicleIndex).padStart(3, '0')}`;
        const id = isDemoMode ? redactVehicleId(vehicleId, isDemoMode) : vehicleId;
        vehicles.push({
          id,
          status: 'Stopped',
          lastUpdate: null,
          speed: null,
          vehicleRegDisplay: standardiseVehicleReg(id)
        });
        vehicleIndex++;
      }

      // Add offline vehicles
      for (let i = 0; i < offlineVehicles; i++) {
        const vehicleId = `VEH-${String(vehicleIndex).padStart(3, '0')}`;
        const id = isDemoMode ? redactVehicleId(vehicleId, isDemoMode) : vehicleId;
        vehicles.push({
          id,
          status: 'Offline',
          lastUpdate: null,
          speed: null,
          vehicleRegDisplay: standardiseVehicleReg(id)
        });
        vehicleIndex++;
      }

      return vehicles;
    }
  })();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <MapPin className="w-6 h-6 text-ring" aria-hidden />
            <h2 className="text-2xl font-bold text-white">Telematics</h2>
          </div>
          <p className="text-muted-foreground mt-1">
            {data && 'data_source' in data && data.data_source === 'realtime'
              ? telematicsRealtimeSubtitle(availableProviders)
              : 'Vehicle location and movement analytics'}
          </p>
          {!useMockData && !isLoading && dataCurrentAsOf && (
            <p className="text-sm text-foreground mt-1">
              Data current as of{' '}
              {formatUkAsOf(dataCurrentAsOf, new Date(), '')}
            </p>
          )}
          {data?.export_timestamp && (
            <p className="text-muted-foreground text-xs mt-1">
              Last updated: {formatUpdatedTime24h(mergedData?.export_timestamp ?? data?.export_timestamp)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {!useMockData && !isLoading && isBackgroundRefreshing && (
            <RefreshCw className="w-3.5 h-3.5 animate-spin text-ring" aria-label="Refreshing" />
          )}
          {isLoading && (
            <div className="text-sm text-ring animate-pulse">
              {loadingMessage || telematicsFallbackLoadingMsg}
            </div>
          )}
          {useMockData ? (
            <ControlCentreSourceBadge label="Mock" />
          ) : (
            <ControlCentreSourceBadge
              selectId="telematics-source"
              label={formatIngestSourceList(availableProviders)}
              options={telematicsSourceOptions}
              value={effectiveTelematicsSource}
              onChange={(v) => setTelematicsSource(v as TelematicsSource)}
            />
          )}
        </div>
      </div>

      {/* KPIs Row 1: Primary Metrics */}
      <div ref={kpiGridRef} className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <ControlCentreKPICard
          id="kpi-total-vehicles"
          title="Total Vehicles"
          value={totalsStable ? totalVehicles : '—'}
          subtitle="Tracked in fleet"
          icon={Truck}
          variant="primary"
          colorScheme="blue"
          onClick={() => totalsStable && setKpiModal({ open: true, title: 'All Vehicles', subtitle: `${totalVehicles} vehicles tracked in fleet` })}
        />

        <ControlCentreKPICard
          id="kpi-active-vehicles"
          title="Active Vehicles"
          value={totalsStable ? activeVehicles : '—'}
          subtitle={totalsStable ? `${activeRate.toFixed(1)}% of fleet` : '—'}
          icon={Activity}
          variant="primary"
          colorScheme="green"
          onClick={() => totalsStable && setKpiModal({ open: true, title: 'Active Vehicles', subtitle: `${activeVehicles} of ${totalVehicles} vehicles active`, filterFn: (v) => /moving|idle|online|active/i.test(String(v.status || '')) })}
        />

        <ControlCentreKPICard
          id="kpi-prolonged-idle"
          title="Idling ≥15 min"
          value={totalsStable ? prolongedIdleVehicles : '—'}
          subtitle={
            !totalsStable
              ? '—'
              : (() => {
                  const insufficient = Number(
                    (mergedData as { idle_with_insufficient_history?: number } | null)
                      ?.idle_with_insufficient_history,
                  ) || 0;
                  if (prolongedIdleVehicles === 0 && insufficient > 0) {
                    return `${insufficient} idle spell${insufficient === 1 ? '' : 's'} unproven`;
                  }
                  return 'Continuous idle';
                })()
          }
          icon={Clock}
          variant="primary"
          colorScheme="amber"
          onClick={() => totalsStable && setKpiModal({
            open: true,
            title: 'Idling ≥15 min',
            subtitle: `${prolongedIdleVehicles} vehicle${prolongedIdleVehicles === 1 ? '' : 's'} still idle for 15+ minutes`,
            filterFn: (v) => v.prolonged_idle === true,
          })}
        />
      </div>

      {/* Vehicle Status Table and GPS Map - Side by Side */}
      <div ref={vehicleStatusRef} className="grid grid-cols-1 lg:grid-cols-2 gap-6 scroll-mt-16">
        {/* Vehicle Status Table - Left Side (50% width) */}
        <div className="w-full">
          <DataTable
            title={
              <div className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-ring" />
                <span>Vehicle Status</span>
              </div>
            }
            hoverColor="blue"
            columns={[
              { key: 'id_display', label: 'Vehicle Reg', align: 'left' },
              { key: 'status_display', label: 'Status', align: 'left' },
              { key: 'speed_display', label: 'Speed', align: 'right' },
              { key: 'data_age_display', label: 'Last Updated', align: 'left' },
            ]}
            customColumnWidths={{
              id_display: { minWidth: 15, maxWidth: 22 }, // More space for Vehicle Reg
              status_display: { minWidth: 10, maxWidth: 16 },
              speed_display: { minWidth: 6, maxWidth: 10 },
              data_age_display: { minWidth: 8, maxWidth: 12 },
            }}
            defaultSort={{ column: 'data_age_display', direction: 'asc' }}
            data={(() => {
              void dataAgeTick;
              const rows = vehicleStatusList.map((vehicle) => {
                const hasGPS = (data?.vehicles ?? mergedData?.vehicles)?.some((v: { id?: string; latitude?: number | null; longitude?: number | null; lat?: number | null; lng?: number | null }) =>
                  v.id === vehicle.id &&
                  (v.latitude ?? v.lat) != null &&
                  (v.longitude ?? v.lng) != null &&
                  !isNaN(Number(v.latitude ?? v.lat)) &&
                  !isNaN(Number(v.longitude ?? v.lng)) &&
                  (v.latitude ?? v.lat) !== 0 &&
                  (v.longitude ?? v.lng) !== 0
                );
                const dataAge = getDataAge(vehicle.lastUpdate);
                const speedInfo = formatSpeed(vehicle.speed);
                return {
                  id: vehicle.id,
                  id_display: vehicle.vehicleRegDisplay,
                  status: vehicle.status,
                  status_display: getStatusChip(vehicle.status, {
                    prolongedIdle: vehicle.prolonged_idle === true,
                    idleDurationMinutes: vehicle.idle_duration_minutes,
                    idleDurationTruncated: vehicle.idle_duration_truncated === true,
                  }),
                  status_display_sortKey: vehicle.prolonged_idle
                    ? `idle-prolonged-${String(vehicle.idle_duration_minutes ?? 15).padStart(4, '0')}`
                    : vehicle.status,
                  speed_display: speedInfo.display,
                  speed_display_sortKey: speedInfo.sortKey,
                  data_age_display: (
                    <span className={dataAge.color}>
                      {dataAge.display}
                    </span>
                  ),
                  data_age_display_sortKey: dataAge.sortKey,
                  _onClick: hasGPS ? () => handleVehicleRowClick(vehicle.id) : undefined,
                  _hasGPS: hasGPS,
                };
              });
              // Default order: Last Updated ascending ("Just Now" first), then Vehicle Reg ascending
              return rows.sort((a, b) => {
                const ageA = a.data_age_display_sortKey as number;
                const ageB = b.data_age_display_sortKey as number;
                if (ageA !== ageB) return ageA - ageB;
                return String(a.id).localeCompare(String(b.id), undefined, { numeric: true });
              });
            })()}
            onRowClick={(row) => {
              // Fallback: if _onClick is not set, try to find vehicle by id
              if (!row._onClick && row.id) {
                handleVehicleRowClick(String(row.id));
              }
            }}
            selectedRowId={selectedVehicleId}
            rowIdKey="id"
            maxRows={10}
            equalWidthColumns={false}
            hideExport={false}
          />
        </div>

        {/* GPS Map - Right Side (50% width) */}
        <div className="w-full">
          <div className="bg-card rounded-lg shadow-sm border border-border h-full min-h-125">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <MapPin className="w-5 h-5 text-success shrink-0" />
                  <span className="text-lg font-semibold text-white">Live GPS Tracking</span>
                </div>
                <div
                  role="group"
                  aria-label="Map colour theme"
                  className="inline-flex items-center gap-2 shrink-0"
                >
                  <Sun className={`w-3.5 h-3.5 ${mapTheme === 'light' ? 'text-warning' : 'text-muted-foreground'}`} aria-hidden />
                  <Button
                    variant="ghost"
                    type="button"
                    role="switch"
                    aria-checked={mapTheme === 'dark'}
                    aria-label={mapTheme === 'dark' ? 'Switch map to light mode' : 'Switch map to dark mode'}
                    onClick={() => setMapTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
                    className={`relative h-6 w-11 rounded-full p-0 focus-visible:ring-success ${
                      mapTheme === 'dark' ? 'bg-primary' : 'bg-muted-foreground'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                        mapTheme === 'dark' ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </Button>
                  <Moon className={`w-3.5 h-3.5 ${mapTheme === 'dark' ? 'text-success' : 'text-muted-foreground'}`} aria-hidden />
                  <span className="text-xs text-muted-foreground tabular-nums w-9">
                    {mapTheme === 'dark' ? 'Dark' : 'Light'}
                  </span>
                </div>
              </div>
              <p className="text-muted-foreground text-sm mt-1">
                {`Real-time vehicle locations on map`}
              </p>
            </div>
            <div className="relative w-full h-map-with-scrubber">
              {vehiclesWithGPS.length === 0 ? (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <Truck className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground">No GPS data available</p>
                    <p className="text-muted-foreground text-sm mt-1">
                      Vehicle positions will appear here when available
                    </p>
                  </div>
                </div>
              ) : (
                <MapComponent
                  center={[mapCenter.lon, mapCenter.lat]}
                  zoom={mapCenter.zoom}
                  theme={mapTheme}
                  styles={{
                    // Colourful road map (parks, highways) — closer to familiar Google Maps
                    // than Carto Positron. Dark keeps the default Control Centre basemap.
                    // If OpenFreeMap is blocked/offline, Map falls back to Carto Voyager.
                    light: 'https://tiles.openfreemap.org/styles/liberty',
                    lightFallback:
                      'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
                  }}
                >
                  <MapControls
                    position="bottom-right"
                    showZoom
                    showCompass
                    showLocate
                    showFullscreen
                  />
                  <MapController vehicles={vehiclesWithGPS} selectedVehicleId={selectedVehicleId} depots={depotData} selectedDepotIndex={selectedDepotIndex} />
                  {/* Render non-selected vehicles first */}
                  {vehiclesWithGPS
                    .filter(v => selectedVehicleId !== v.id)
                    .map((vehicle, index) => {
                      const statusColor = 
                        vehicle.status?.toLowerCase() === 'moving' ? STATUS_COLORS.success :
                        vehicle.status?.toLowerCase() === 'idle' ? STATUS_COLORS.warning :
                        vehicle.status?.toLowerCase() === 'stopped' ? STATUS_COLORS.destructive :
                        CHART_PALETTE[7];
                      const vehicleRegDisplay = getVehicleRegDisplay(vehicle.id, inferVehicleSource(vehicle as { source?: string }));
                      const speedLabel = formatSpeedMphDetail(vehicle.speed);

                      return (
                        <MapMarker
                          key={vehicle.id || `vehicle-${index}`}
                          longitude={vehicle.longitude!}
                          latitude={vehicle.latitude!}
                        >
                        <MarkerContent>
                          <div 
                            className="relative cursor-pointer flex items-center justify-center"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedVehicleId(vehicle.id);
                              // Popup will open automatically on marker click
                            }}
                          >
                            {/* Filled circular background */}
                            <div 
                              className="absolute rounded-full transition-all drop-shadow-lg"
                              style={{
                                width: '36px',
                                height: '36px',
                                backgroundColor: statusColor,
                                boxShadow: `0 0 8px ${statusColor}80, 0 2px 4px rgba(0,0,0,0.3)`,
                              }}
                            />
                            {/* White truck icon on top */}
                            <Truck 
                              className="relative transition-all"
                              style={{ 
                                color: 'white',
                                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
                                transform: 'scale(1)',
                              }}
                              size={20}
                            />
                          </div>
                        </MarkerContent>
                        {/* Always render popup for all vehicles - it will open on marker click */}
                        <MarkerPopup className="vehicle-tooltip" closeButton={true}>
                          <div className="px-2 py-1.5 space-y-1">
                            <p className="font-medium text-white">{vehicleRegDisplay}</p>
                            <p className="text-xs text-foreground">
                              Status: <span style={{ color: statusColor }}>{formatVehicleStatus(vehicle.status)}</span>
                            </p>
                            {speedLabel != null && (
                              <p className="text-xs text-foreground">
                                Speed: {speedLabel}
                              </p>
                            )}
                            {vehicle.lastUpdate && (
                              <p className="text-xs text-muted-foreground">
                                Updated: {formatUpdatedTime24h(vehicle.lastUpdate)}
                              </p>
                            )}
                          </div>
                        </MarkerPopup>
                        {/* Tooltip for hover */}
                        <MarkerTooltip className="vehicle-tooltip">
                          <div className="px-2 py-1.5 space-y-1">
                            <p className="font-medium text-white">{vehicleRegDisplay}</p>
                            <p className="text-xs text-foreground">
                              Status: <span style={{ color: statusColor }}>{formatVehicleStatus(vehicle.status)}</span>
                            </p>
                            {speedLabel != null && (
                              <p className="text-xs text-foreground">
                                Speed: {speedLabel}
                              </p>
                            )}
                            {vehicle.lastUpdate && (
                              <p className="text-xs text-muted-foreground">
                                Updated: {formatUpdatedTime24h(vehicle.lastUpdate)}
                              </p>
                            )}
                          </div>
                        </MarkerTooltip>
                      </MapMarker>
                    );
                  })}
                  {/* Render selected vehicle last so it appears on top */}
                  {vehiclesWithGPS
                    .filter(v => selectedVehicleId === v.id)
                    .map((vehicle, index) => {
                      const statusColor = 
                        vehicle.status?.toLowerCase() === 'moving' ? STATUS_COLORS.success :
                        vehicle.status?.toLowerCase() === 'idle' ? STATUS_COLORS.warning :
                        vehicle.status?.toLowerCase() === 'stopped' ? STATUS_COLORS.destructive :
                        CHART_PALETTE[7];
                      const vehicleRegDisplay = getVehicleRegDisplay(vehicle.id, inferVehicleSource(vehicle as { source?: string }));
                      const speedLabel = formatSpeedMphDetail(vehicle.speed);

                      return (
                        <MapMarker
                          key={vehicle.id || `selected-vehicle-${index}`}
                          longitude={vehicle.longitude!}
                          latitude={vehicle.latitude!}
                        >
                          <MarkerContent>
                            <div 
                              className="relative cursor-pointer flex items-center justify-center"
                              style={{ zIndex: 1000 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedVehicleId(vehicle.id);
                                // Popup will open automatically on marker click
                              }}
                            >
                              {/* Filled circular background - larger for selected */}
                              <div 
                                className="absolute rounded-full transition-all drop-shadow-lg"
                                style={{
                                  width: '50px',
                                  height: '50px',
                                  backgroundColor: statusColor,
                                  boxShadow: `0 0 16px ${statusColor}, 0 0 8px ${statusColor}80, 0 4px 8px rgba(0,0,0,0.4)`,
                                }}
                              />
                              {/* White truck icon on top - larger for selected */}
                              <Truck 
                                className="relative transition-all"
                                style={{ 
                                  color: 'white',
                                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))',
                                  transform: 'scale(1)',
                                }}
                                size={28}
                              />
                              {/* Pulsing ripple effect around selected vehicle */}
                              <div 
                                className="absolute inset-0 rounded-full animate-ping"
                                style={{
                                  border: `3px solid ${statusColor}`,
                                  width: '64px',
                                  height: '64px',
                                  margin: '-7px',
                                  opacity: 0.6,
                                }}
                              />
                            </div>
                          </MarkerContent>
                          {/* Always render popup for all vehicles - it will open on marker click */}
                          <MarkerPopup className="vehicle-tooltip" closeButton={true}>
                            <div className="px-2 py-1.5 space-y-1">
                              <p className="font-medium text-white">{vehicleRegDisplay}</p>
                              <p className="text-xs text-foreground">
                                Status: <span style={{ color: statusColor }}>{formatVehicleStatus(vehicle.status)}</span>
                              </p>
                              {speedLabel != null && (
                                <p className="text-xs text-foreground">
                                  Speed: {speedLabel}
                                </p>
                              )}
                              {vehicle.lastUpdate && (
                                <p className="text-xs text-muted-foreground">
                                  Updated: {formatUpdatedTime24h(vehicle.lastUpdate)}
                                </p>
                              )}
                            </div>
                          </MarkerPopup>
                          {/* Tooltip for hover */}
                          <MarkerTooltip className="vehicle-tooltip">
                            <div className="px-2 py-1.5 space-y-1">
                              <p className="font-medium text-white">{vehicleRegDisplay}</p>
                              <p className="text-xs text-foreground">
                                Status: <span style={{ color: statusColor }}>{formatVehicleStatus(vehicle.status)}</span>
                              </p>
                              {speedLabel != null && (
                                <p className="text-xs text-foreground">
                                  Speed: {speedLabel}
                                </p>
                              )}
                              {vehicle.lastUpdate && (
                                <p className="text-xs text-muted-foreground">
                                  Updated: {formatUpdatedTime24h(vehicle.lastUpdate)}
                                </p>
                              )}
                            </div>
                          </MarkerTooltip>
                        </MapMarker>
                      );
                    })}
                  {/* Depot markers */}
                  {depotData.map((depot, index) => {
                    // Coordinates are [lat, lon] in the JSON file
                    const [lat, lon] = depot.coordinates;
                    const isSelected = selectedDepotIndex === index;
                    return (
                      <MapMarker
                        key={`depot-${index}`}
                        longitude={lon}
                        latitude={lat}
                      >
                        <MarkerContent>
                          <div 
                            className="relative cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedDepotIndex(index);
                              setSelectedVehicleId(null); // Clear vehicle selection when depot is selected
                            }}
                          >
                            <svg
                              width={isSelected ? "48" : "36"}
                              height={isSelected ? "64" : "48"}
                              viewBox="0 0 24 32"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="drop-shadow-lg transition-all"
                              style={{ 
                                filter: isSelected ? 'drop-shadow(0 0 8px rgba(59,130,246,0.8))' : 'drop-shadow(0 0 3px rgba(255,255,255,0.8))'
                              }}
                            >
                              {/* Pin shape */}
                              <path
                                d="M12 0C7.58 0 4 3.58 4 8C4 14 12 24 12 24C12 24 20 14 20 8C20 3.58 16.42 0 12 0Z"
                                fill={DEPOT_MARKER_COLOR}
                                stroke="white"
                                strokeWidth={isSelected ? "2" : "1.5"}
                              />
                              {/* Warehouse building icon */}
                              <rect x="7" y="10" width="10" height="8" fill="white" stroke={DEPOT_MARKER_COLOR} strokeWidth="0.5" />
                              <rect x="8" y="12" width="2" height="2" fill={DEPOT_MARKER_COLOR} />
                              <rect x="11" y="12" width="2" height="2" fill={DEPOT_MARKER_COLOR} />
                              <rect x="14" y="12" width="2" height="2" fill={DEPOT_MARKER_COLOR} />
                              <line x1="7" y1="14" x2="17" y2="14" stroke={DEPOT_MARKER_COLOR} strokeWidth="0.5" />
                              <polygon points="9,10 12,7 15,10" fill="white" />
                            </svg>
                            {/* Pulsing effect for selected depot */}
                            {isSelected && (
                              <div 
                                className="absolute inset-0 rounded-full animate-ping"
                                style={{
                                  border: `3px solid ${DEPOT_MARKER_COLOR}`,
                                  width: '72px',
                                  height: '72px',
                                  margin: '-12px',
                                  opacity: 0.6,
                                }}
                              />
                            )}
                          </div>
                        </MarkerContent>
                        <MarkerPopup className="vehicle-tooltip" closeButton={true}>
                          <div className="px-2 py-1.5">
                            <p className="font-medium text-white">{depot.name}</p>
                          </div>
                        </MarkerPopup>
                        <MarkerTooltip className="vehicle-tooltip">
                          <div className="px-2 py-1.5">
                            <p className="font-medium text-white">{depot.name}</p>
                          </div>
                        </MarkerTooltip>
                      </MapMarker>
                    );
                  })}
                  {/* Conditional popup when depot is selected */}
                  {selectedDepotIndex !== null && (() => {
                    const selectedDepot = depotData[selectedDepotIndex];
                    if (selectedDepot) {
                      const [lat, lon] = selectedDepot.coordinates;
                      return (
                        <MapPopup
                          longitude={lon}
                          latitude={lat}
                          closeButton={true}
                          onClose={() => setSelectedDepotIndex(null)}
                          className="vehicle-tooltip"
                        >
                          <div className="px-2 py-1.5">
                            <p className="font-medium text-white">{selectedDepot.name}</p>
                          </div>
                        </MapPopup>
                      );
                    }
                    return null;
                  })()}
                  {/* Conditional popup when vehicle is selected from table */}
                  {selectedVehicleId && (() => {
                    const selectedVehicle = vehiclesWithGPS.find(v => v.id === selectedVehicleId);
                    if (selectedVehicle && selectedVehicle.latitude != null && selectedVehicle.longitude != null) {
                      const selectedStatusColor =
                        selectedVehicle.status?.toLowerCase() === 'moving' ? STATUS_COLORS.success :
                        selectedVehicle.status?.toLowerCase() === 'idle' ? STATUS_COLORS.warning :
                        selectedVehicle.status?.toLowerCase() === 'stopped' ? STATUS_COLORS.destructive :
                        CHART_PALETTE[7];
                      const selectedVehicleRegDisplay = getVehicleRegDisplay(selectedVehicle.id, inferVehicleSource(selectedVehicle as { source?: string }));
                      const selectedSpeedLabel = formatSpeedMphDetail(selectedVehicle.speed);
                      return (
                        <MapPopup
                          longitude={selectedVehicle.longitude}
                          latitude={selectedVehicle.latitude}
                          closeButton={true}
                          onClose={() => setSelectedVehicleId(null)}
                          className="vehicle-tooltip"
                        >
                          <div className="px-2 py-1.5 space-y-1">
                            <p className="font-medium text-white">{selectedVehicleRegDisplay}</p>
                            <p className="text-xs text-foreground">
                              Status: <span style={{ color: selectedStatusColor }}>{formatVehicleStatus(selectedVehicle.status)}</span>
                            </p>
                            {selectedSpeedLabel != null && (
                              <p className="text-xs text-foreground">
                                Speed: {selectedSpeedLabel}
                              </p>
                            )}
                            {selectedVehicle.lastUpdate && (
                              <p className="text-xs text-muted-foreground">
                                Updated: {formatUpdatedTime24h(selectedVehicle.lastUpdate)}
                              </p>
                            )}
                          </div>
                        </MapPopup>
                      );
                    }
                    return null;
                  })()}
                </MapComponent>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* KPI Detail Modal */}
      <KPIDataModal
        isOpen={kpiModal.open}
        onClose={() => {
          setKpiModal({ open: false, title: '' });
          onHighlightConsumed?.();
        }}
        title={kpiModal.title}
        subtitle={kpiModal.subtitle}
        icon={MapPin}
        data={vehicleStatusList}
        columns={VEHICLE_COLUMNS}
        filterFn={kpiModal.filterFn}
        searchKeys={['id', 'status']}
        exportFilename={`vehicles_${new Date().toISOString().split('T')[0]}.csv`}
      />
    </div>
  );
}